import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Badge,
  Icon,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
  useBreakpointValue,
  Card,
  CardBody,
  CardFooter,
  Divider,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { COLORS, GRADIENTS } from '../utils/constants';
import { 
  FaWallet, 
  FaImage, 
  FaDollarSign, 
  FaTrophy, 
  FaArrowRight,
  FaCheck,
  FaCoins,
  FaRocket,
  FaCog,
  FaVideo,
  FaMagic,
  FaDownload,
  FaRobot,
  FaQuestionCircle,
  FaSync,
  FaLightbulb,
  FaUserFriends,
  FaLayerGroup,
  FaCamera
} from 'react-icons/fa';

// Hexagon component for visual identity (reused from Home.js)
const Hexagon = ({ size, position, delay, opacity, color, rotationSpeed }) => (
  <Box
    position="absolute"
    width={size}
    height={size}
    {...position}
    opacity={opacity}
    zIndex="0"
    className="hexagon-animation"
    sx={{
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'transparent',
        border: `1px solid ${color || COLORS.primary}`,
        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
        animation: `rotate ${rotationSpeed || 60}s linear infinite`,
        animationDelay: `${delay || 0}s`,
        opacity: 0.6
      },
      '@keyframes rotate': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
      }
    }}
  />
);

// Feature Card component
const FeatureCard = ({ title, icon, description }) => (
  <Box
    borderWidth="1px"
    borderColor={COLORS.primary}
    borderRadius="lg"
    overflow="hidden"
    bg="rgba(0, 0, 0, 0.6)"
    p={5}
    transition="all 0.3s"
    _hover={{
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 25px rgba(0, 168, 107, 0.3)'
    }}
    className="glow-border"
  >
    <VStack spacing={4} align="flex-start">
      <Icon as={icon} color={COLORS.primary} boxSize={8} />
      <Heading size="md" color={COLORS.accent}>{title}</Heading>
      <Text color="white">{description}</Text>
    </VStack>
  </Box>
);

function Tutorial() {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Generation flow steps with shorter descriptions
  const generationSteps = [
    { title: 'Enter Prompt', description: 'Write your image description', icon: FaRocket },
    { title: 'Select Options', description: 'Choose image settings', icon: FaLayerGroup },
    { title: 'Pay with XGCL', description: 'Use tokens for generation', icon: FaCoins },
    { title: 'Receive Image', description: 'Download your creation', icon: FaCamera }
  ];
  
  // Video generation flow
  const videoGenerationSteps = [
    { title: 'Describe Video', description: 'Detail your video concept', icon: FaVideo },
    { title: 'Set Parameters', description: 'Choose duration and style', icon: FaCog },
    { title: 'Generate', description: 'Create your video', icon: FaMagic },
    { title: 'Download/Share', description: 'Use your video', icon: FaDownload }
  ];
  
  // AI Chat flow
  const aiChatSteps = [
    { title: 'Start Chat', description: 'Begin a conversation', icon: FaRobot },
    { title: 'Ask Questions', description: 'Get creative assistance', icon: FaQuestionCircle },
    { title: 'Refine Ideas', description: 'Iterate with AI', icon: FaSync },
    { title: 'Apply Insights', description: 'Use in your projects', icon: FaLightbulb }
  ];

  const { activeStep } = useSteps({
    index: 1,
    count: generationSteps.length,
  });

  // Responsive adjustments - moved outside of render
  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3 });
  const stepperSize = useBreakpointValue({ base: "sm", md: "md", lg: "lg" });
  const stepperGap = useBreakpointValue({ base: 2, md: 4, lg: 8 });
  const stepperOrientation = useBreakpointValue({ base: "vertical", md: "horizontal" });
  const stepBoxWidth = useBreakpointValue({ base: "auto", md: "140px" });
  const titleFontSize = useBreakpointValue({ base: "sm", md: "md" });
  const descriptionFontSize = useBreakpointValue({ base: "xs", md: "sm" });

  return (
    <Box overflow="hidden" position="relative">
      {/* Strategic hexagon decorations */}
      {/* <Hexagon size="350px" position={{ top: "-50px", left: "-150px" }} opacity={0.15} rotationSpeed={120} />
      <Hexagon size="250px" position={{ bottom: "20%", right: "-100px" }} opacity={0.1} delay={2} />
      <Hexagon size="180px" position={{ top: "40%", left: "10%" }} opacity={0.08} rotationSpeed={90} delay={4} />
       */}
      <Container maxW="7xl" py={8}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center" py={10}>
            <Badge 
              px={4} 
              py={2} 
              mb={4} 
              borderRadius="full" 
              fontSize="md"
              bg="rgba(30, 220, 11, 0.15)"
              color={COLORS.primary}
            >
              Platform Guide
            </Badge>
            <Heading 
              as="h1" 
              size="2xl" 
              color="white"
              fontWeight="bold"
              mb={6}
            >
              How ignita Works
            </Heading>
            <Text 
              fontSize="xl" 
              color="white" 
              maxW="3xl" 
              mx="auto"
              mb={8}
            >
              A comprehensive guide to using the ignita platform for AI image generation, video creation, and AI chat assistance.
              Learn all the features and get the most out of our AI-powered creation tools.
            </Text>
            <Button
              size="lg"
              className="primary-button"
              onClick={() => navigate('/generate')}
              rightIcon={<FaArrowRight />}
            >
              Try It Now
            </Button>
          </Box>

          {/* What is ignita Section */}
          <Box py={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              textAlign="center"
              color={COLORS.accent}
            >
              What is ignita?
            </Heading>
            <Grid templateColumns={`repeat(${columns}, 1fr)`} gap={8}>
              <FeatureCard 
                title="AI Image Generation"
                icon={FaImage}
                description="Create stunning images from text descriptions with customizable styles and settings."
              />
              <FeatureCard 
                title="Video Generation"
                icon={FaVideo}
                description="Transform your ideas into engaging videos with various durations and styles."
              />
              <FeatureCard 
                title="AI Chat Assistant"
                icon={FaRobot}
                description="Get creative help, answer questions, and explore ideas with our intelligent AI."
              />
              <FeatureCard 
                title="NFT Maker"
                icon={FaImage}
                description="Upload your base artwork, define traits and variations, then generate thousands of unique NFTs."
              />
              <FeatureCard 
                title="Crypto Integration"
                icon={FaWallet}
                description="Powered by XGCL cryptocurrency, providing secure, fast transactions for all your creative needs."
              />

  
            </Grid>
          </Box>

          {/* XGCL Coin Utility Section */}
          <Box py={8} bg="rgba(10, 24, 10, 0.7)" borderRadius="lg" p={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              textAlign="center"
              color={COLORS.accent}
            >
              XGCL Utility
            </Heading>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={8} alignItems="center">
              <Box>
                <List spacing={4}>
                  <ListItem>
                    <HStack>
                      <Icon as={FaCheck} color={COLORS.primary} boxSize={5} />
                      <Text color="white" fontSize="md">Purchase generation credits at tiered rates</Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack>
                      <Icon as={FaCheck} color={COLORS.primary} boxSize={5} />
                      <Text color="white" fontSize="md">Pay for premium templates and effects</Text>
                    </HStack>
                  </ListItem>
                </List>
              </Box>
              <Box 
                borderWidth="1px"
                borderColor={COLORS.primary}
                borderRadius="lg"
                p={6}
                className="glow-border"
                bg="rgba(0, 0, 0, 0.6)"
              >
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color={COLORS.accent}>XGCL Token Economics</Heading>
                  <HStack justify="space-between">
                    <Text color="white">Standard</Text>
                    <Badge colorScheme="green">10 Credit</Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="white">Standard HD</Text>
                    <Badge colorScheme="green">25 Credits</Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="white">Landscape/Portrait HD</Text>
                    <Badge colorScheme="green">25 Credits</Badge>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="white">Video Generation (5s)</Text>
                    <Badge colorScheme="green">120 Credits</Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="white">Video Generation (10s)</Text>
                    <Badge colorScheme="green">240 Credits</Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="white">AI Chat Messages</Text>
                    <Badge colorScheme="green">1 Credits</Badge>
                  </HStack>
                  <Divider borderColor="rgba(30, 220, 11, 0.3)" />
                  <HStack justify="space-between">
                    <Text color="white" fontWeight="bold">Bulk Purchase Bonus</Text>
                    <Badge colorScheme="green" variant="solid">+20-28%</Badge>
                  </HStack>
                </VStack>
              </Box>
            </Grid>
          </Box>

          {/* Image Generation Flow */}
          <Box py={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={10}
              textAlign="center"
              color={COLORS.accent}
            >
              Image Generation Flow
            </Heading>
            <Box maxW="900px" mx="auto" px={4}>
              <Stepper 
                index={activeStep} 
                colorScheme="green" 
                size={stepperSize}
                gap={stepperGap}
                orientation={stepperOrientation}
              >
                {generationSteps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator>
                      <StepStatus 
                        complete={<Icon as={step.icon} color={COLORS.primary} />} 
                        incomplete={<StepNumber />} 
                        active={<Icon as={step.icon} color={COLORS.primary} />} 
                      />
                    </StepIndicator>
                    <Box flexShrink="0" minW={stepBoxWidth}>
                      <StepTitle color={COLORS.accent} fontSize={titleFontSize}>{step.title}</StepTitle>
                      <StepDescription color="white" fontWeight="medium" fontSize={descriptionFontSize}>{step.description}</StepDescription>
                    </Box>
                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>

          {/* Video Generation Flow */}
          <Box py={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              textAlign="center"
              color={COLORS.accent}
            >
              Video Generation Flow
            </Heading>
            <Box maxW="5xl" mx="auto">
              <Stepper
                index={1}
                colorScheme="green"
                size={stepperSize}
                gap={stepperGap}
                orientation={stepperOrientation}
              >
                {videoGenerationSteps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator>
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<Icon as={step.icon} />}
                        active={<Icon as={step.icon} />}
                      />
                    </StepIndicator>
                    <Box flexShrink="0" width={stepBoxWidth}>
                      <StepTitle fontSize={titleFontSize}>{step.title}</StepTitle>
                      <StepDescription fontSize={descriptionFontSize}>{step.description}</StepDescription>
                    </Box>
                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box 
              mt={10} 
              bg="rgba(30, 220, 11, 0.15)" 
              p={6} 
              borderRadius="lg"
              borderWidth="1px"
              borderColor="rgba(30, 220, 11, 0.3)"
              maxW="5xl"
              mx="auto"
            >
              <Text color="white" textAlign="center">
                Create dynamic videos with our AI video generator. Choose duration, style, and resolution to bring your ideas to life. 
                Perfect for social media, presentations, or creative projects.
              </Text>
            </Box>
          </Box>
          
          {/* AI Chat Flow */}
          <Box py={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              textAlign="center"
              color={COLORS.accent}
            >
              AI Chat Assistant
            </Heading>
            <Box maxW="5xl" mx="auto">
              <Stepper
                index={1}
                colorScheme="green"
                size={stepperSize}
                gap={stepperGap}
                orientation={stepperOrientation}
              >
                {aiChatSteps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator>
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<Icon as={step.icon} />}
                        active={<Icon as={step.icon} />}
                      />
                    </StepIndicator>
                    <Box flexShrink="0" width={stepBoxWidth}>
                      <StepTitle fontSize={titleFontSize}>{step.title}</StepTitle>
                      <StepDescription fontSize={descriptionFontSize}>{step.description}</StepDescription>
                    </Box>
                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box 
              mt={10} 
              bg="rgba(30, 220, 11, 0.15)" 
              p={6} 
              borderRadius="lg"
              borderWidth="1px"
              borderColor="rgba(30, 220, 11, 0.3)"
              maxW="5xl"
              mx="auto"
            >
              <Text color="white" textAlign="center">
                Our AI chat assistant helps with creative brainstorming, technical questions, and project planning.
                Use it to refine your ideas, improve prompts, or get feedback on your creations.
              </Text>
            </Box>
          </Box>

          {/* XGCL Payment UX Section */}
          <Box py={8} bg="rgba(10, 24, 10, 0.7)" borderRadius="lg" p={8}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              textAlign="center"
              color={COLORS.accent}
            >
              XGCL Payment Experience
            </Heading>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={8}>
              <VStack align="stretch" spacing={4}>
                <Box 
                  borderWidth="1px"
                  borderColor={COLORS.primary}
                  borderRadius="lg"
                  p={4}
                  className="glow-border"
                  bg="rgba(0, 0, 0, 0.6)"
                >
                  <Heading size="md" color={COLORS.accent} mb={3}>Step 1: Connect Wallet</Heading>
                  <Text color="white">
                    Seamlessly connect your XRP Ledger wallet through XAMAN integration. One-click secure authentication allows you to access your XGCL balance instantly.
                  </Text>
                </Box>
                <Box 
                  borderWidth="1px"
                  borderColor={COLORS.primary}
                  borderRadius="lg"
                  p={4}
                  className="glow-border"
                  bg="rgba(0, 0, 0, 0.6)"
                >
                  <Heading size="md" color={COLORS.accent} mb={3}>Step 2: Select Credit Package</Heading>
                  <Text color="white">
                    Choose from pre-configured credit packages or customize your amount. Larger packages provide bonus credits, maximizing the value of your XGCL.
                  </Text>
                </Box>
                <Box 
                  borderWidth="1px"
                  borderColor={COLORS.primary}
                  borderRadius="lg"
                  p={4}
                  className="glow-border"
                  bg="rgba(0, 0, 0, 0.6)"
                >
                  <Heading size="md" color={COLORS.accent} mb={3}>Step 3: Confirm Payment</Heading>
                  <Text color="white">
                    Review and approve the transaction through your XAMAN wallet. The platform automatically detects your payment and credits your account instantly.
                  </Text>
                </Box>
              </VStack>
              {/* <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                borderWidth="1px"
                borderColor={COLORS.primary}
                borderRadius="lg"
                p={6}
                className="glow-border"
                bg="rgba(0, 0, 0, 0.6)"
              >
                <Image 
                  src="/images/wallet-mockup.png" 
                  alt="XGCL Wallet Connection" 
                  fallbackSrc="https://via.placeholder.com/400x500?text=Wallet+Integration"
                  maxH="400px"
                  borderRadius="md"
                />
              </Box> */}
            </Grid>
          </Box>



          {/* Monetization Options Section */}
          {/* <Box py={8} bg="rgba(10, 24, 10, 0.7)" borderRadius="lg" p={8}>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
              <Box
                borderWidth="1px"
                borderColor={COLORS.primary}
                borderRadius="lg"
                p={5}
                className="glow-border"
                bg="rgba(0, 0, 0, 0.6)"
                textAlign="center"
              >
                <Icon as={FaImage} color={COLORS.primary} boxSize={10} mb={4} />
                <Heading size="md" color={COLORS.accent} mb={3}>Sell Generated Art</Heading>
                <Text color="white">
                  Create and sell unique AI-generated images to clients or on NFT marketplaces. Each creation is yours to monetize.
                </Text>
              </Box>
              
              <Box
                borderWidth="1px"
                borderColor={COLORS.primary}
                borderRadius="lg"
                p={5}
                className="glow-border"
                bg="rgba(0, 0, 0, 0.6)"
                textAlign="center"
              >
                <Icon as={FaDollarSign} color={COLORS.primary} boxSize={10} mb={4} />
                <Heading size="md" color={COLORS.accent} mb={3}>Commercial Use</Heading>
                <Text color="white">
                  Use ignita-generated content for advertising, marketing, and promotional materials. Perfect for businesses and agencies.
                </Text>
              </Box>
              

            </Grid>
          </Box> */}

          {/* Call to Action */}
          {/* <Box textAlign="center" py={12}>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              color={COLORS.accent}
            >
              Ready to Apply What You've Learned?
            </Heading>
            <Text 
              fontSize="lg" 
              color="white" 
              maxW="2xl" 
              mx="auto"
              mb={8}
            >
              Now that you understand how ignita works, put your knowledge into practice and start creating your own AI-generated masterpieces.
            </Text>
            <HStack spacing={4} justify="center">
              <Button
                size="lg"
                className="primary-button"
                onClick={() => navigate('/generate')}
                leftIcon={<FaImage />}
                color="white"
              >
                Start Creating
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor={COLORS.primary}
                color="white"
                _hover={{
                  bg: "rgba(30, 220, 11, 0.1)",
                  transform: 'translateY(-2px)',
                }}
                onClick={() => navigate('/buy-credits')}
                leftIcon={<FaCoins />}
              >
                Buy XGCL Credits
              </Button>
            </HStack>
          </Box> */}
        </VStack>
      </Container>
    </Box>
  );
}

export default Tutorial; 